import { useState } from "react";
import { Plus, Pencil, Power } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "../components/common/PageHeader";
import { DataTable } from "../components/common/DataTable";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";

export default function SealTypeMaster() {
  const { has } = useAuth();
  const canManage = has("SEAL_TYPE.MANAGE");
  const [form, setForm] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const load = (params) => api.get("/seal-types", { params: { ...params, include_inactive: true } }).then((r) => r.data);
  const save = async () => {
    if (!form.name?.trim() || (!form.id && !form.seal_type_code?.trim())) { toast.error("Code and name required"); return; }
    try {
      if (form.id) await api.put(`/seal-types/${form.id}`, { name: form.name });
      else await api.post("/seal-types", { seal_type_code: form.seal_type_code, name: form.name });
      toast.success("Saved"); setForm(null); setRefreshKey((k) => k + 1);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const toggle = async (row) => {
    try { await api.patch(`/seal-types/${row.id}/status`, null, { params: { is_active: !row.is_active } }); setRefreshKey((k) => k + 1); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const columns = [
    { key: "seal_type_code", label: "Code", mono: true },
    { key: "name", label: "Name" },
    { key: "is_active", label: "Status", render: (r) => <Badge variant={r.is_active ? "default" : "outline"} className={r.is_active ? "bg-emerald-600" : "text-slate-400"}>{r.is_active ? "Active" : "Inactive"}</Badge> },
  ];

  return (
    <div>
      <PageHeader title="Seal Type Master" subtitle="Governed seal types used during Data Entry"
        actions={canManage && <Button data-testid="seal-type-add" className="bg-blue-600 hover:bg-blue-700" onClick={() => setForm({ seal_type_code: "", name: "" })}><Plus className="mr-1.5 h-4 w-4" /> New Seal Type</Button>} />
      <DataTable testid="seal-type-table" columns={columns} load={load} refreshKey={refreshKey} searchPlaceholder="Search seal types..."
        rowActions={canManage ? (row) => (
          <div className="flex justify-end gap-1">
            <Button data-testid={`seal-type-edit-${row.id}`} variant="ghost" size="sm" onClick={() => setForm({ id: row.id, name: row.name })}><Pencil className="h-4 w-4" /></Button>
            <Button data-testid={`seal-type-toggle-${row.id}`} variant="ghost" size="sm" onClick={() => toggle(row)}><Power className={`h-4 w-4 ${row.is_active ? "text-emerald-600" : "text-slate-400"}`} /></Button>
          </div>
        ) : null} />
      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}><DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="font-display">{form?.id ? "Edit Seal Type" : "New Seal Type"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {!form?.id && <div><Label className="text-xs">Code *</Label><Input data-testid="seal-type-code-input" value={form?.seal_type_code || ""} onChange={(e) => setForm({ ...form, seal_type_code: e.target.value })} className="mt-1" /></div>}
          <div><Label className="text-xs">Name *</Label><Input data-testid="seal-type-name-input" value={form?.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => setForm(null)}>Cancel</Button><Button data-testid="seal-type-save" onClick={save} className="bg-blue-600 hover:bg-blue-700">Save</Button></DialogFooter>
      </DialogContent></Dialog>
    </div>
  );
}
